// Lifted from donaflow app/javascript/entrypoints/embed/client.js @ 2cd71fc (cf-1).
// The embed widget a charity pastes into its own website.
//
//   <script src="https://app.example.com/embed/v1/client.js" async></script>
//   <furniture-collection brand="TOKEN"></furniture-collection>
//
// Everything renders inside a shadow root so the host page's CSS cannot reach
// in and this file's CSS cannot leak out. The card paints immediately in a
// default colour, then retints once the brand's accent resolves.
//
// TWO CONSTRAINTS THAT ARE SILENT BREAKAGES IF CHANGED:
//
//   1. `document.currentScript` is null inside an ES module. This file must
//      stay import-free and be loaded by a classic <script src>. Adding a
//      single `import` here breaks ORIGIN and every request below it.
//
//   2. `document.currentScript.src` reads the ORIGINAL /embed/v1/client.js URL
//      off the tag, not the fingerprinted asset it redirects to. That is how a
//      widget served from the charity's page knows which host to call back to.
const scriptSrc = document.currentScript.src
const ORIGIN = new URL(scriptSrc).origin
const TRANSITION_MS = 300

// Matches --icon-accent's fallback in app/stylesheets/application.scss, so an
// unconfigured brand looks the same in the widget and in the flow.
const DEFAULT_ACCENT = '#08A88A'
const HEX_COLOR = /^#[0-9a-f]{6}$/i

// Inline Phosphor (v2) paths, viewBox 0 0 256 256, fill currentColor. Inlined
// rather than loaded as a webfont so the embed pulls in no second request and
// cannot be broken by a host page's icon font.
const ICONS = {
  couch: '<path d="M240,106.17V72a16,16,0,0,0-16-16H32A16,16,0,0,0,16,72v34.17A16,16,0,0,0,8,120v48a16,16,0,0,0,16,16h8v16a8,8,0,0,0,16,0V184H208v16a8,8,0,0,0,16,0V184h8a16,16,0,0,0,16-16V120A16,16,0,0,0,240,106.17ZM224,104h-8a16,16,0,0,0-16,16v16H136V72h88ZM120,72v64H56V120a16,16,0,0,0-16-16H32V72Zm112,96H24V120H40v24a8,8,0,0,0,8,8H208a8,8,0,0,0,8-8V120h16Z"/>',
  pin: '<path d="M128,64a40,40,0,1,0,40,40A40,40,0,0,0,128,64Zm0,64a24,24,0,1,1,24-24A24,24,0,0,1,128,128Zm0-112a88.1,88.1,0,0,0-88,88c0,31.4,14.51,64.68,42,96.25a254.19,254.19,0,0,0,41.45,38.3,8,8,0,0,0,9.18,0A254.19,254.19,0,0,0,174,200.25c27.45-31.57,42-64.85,42-96.25A88.1,88.1,0,0,0,128,16Zm0,206c-16.53-13-72-60.75-72-118a72,72,0,0,1,144,0C200,161.23,144.53,209,128,222Z"/>',
  arrow: '<path d="M221.66,133.66l-72,72a8,8,0,0,1-11.32-11.32L196.69,136H40a8,8,0,0,1,0-16H196.69L138.34,61.66a8,8,0,0,1,11.32-11.32l72,72A8,8,0,0,1,221.66,133.66Z"/>',
  coins: '<path d="M184,89.57V84c0-25.08-37.83-44-88-44S8,58.92,8,84v40c0,20.89,26.25,37.49,64,42.46V172c0,25.08,37.83,44,88,44s88-18.92,88-44V132C248,111.3,222.58,94.68,184,89.57ZM232,132c0,13.22-30.79,28-72,28-3.73,0-7.43-.13-11.08-.37C170.49,151.77,184,139,184,124V105.74C213.87,110.19,232,122.27,232,132ZM72,150.25V126.46A183.74,183.74,0,0,0,96,128a183.74,183.74,0,0,0,24-1.54v23.79A163,163,0,0,1,96,152,163,163,0,0,1,72,150.25Zm96-40.32V124c0,8.39-12.41,17.4-32,22.87V123.5C148.91,120.37,159.84,115.71,168,109.93ZM96,56c41.21,0,72,14.78,72,28s-30.79,28-72,28S24,97.22,24,84,54.79,56,96,56ZM24,124V109.93c8.16,5.78,19.09,10.44,32,13.57v23.37C36.41,141.4,24,132.39,24,124Zm64,48v-4.17c2.63.1,5.29.17,8,.17,3.88,0,7.67-.13,11.39-.35A121.92,121.92,0,0,0,120,171.41v23.46C100.41,189.4,88,180.39,88,172Zm48,26.25V174.4a179.48,179.48,0,0,0,24,1.6,183.74,183.74,0,0,0,24-1.54v23.79a165.45,165.45,0,0,1-48,0Zm64-3.38V171.5c12.91-3.13,23.84-7.79,32-13.57V172C232,180.39,219.59,189.4,200,194.87Z"/>',
  truck: '<path d="M255.42,117l-14-35A15.93,15.93,0,0,0,226.58,72H192V64a8,8,0,0,0-8-8H32A16,16,0,0,0,16,72V184a16,16,0,0,0,16,16H49a32,32,0,0,0,62,0h50a32,32,0,0,0,62,0h17a16,16,0,0,0,16-16V120A7.94,7.94,0,0,0,255.42,117ZM192,88h34.58l9.6,24H192ZM32,72H176v64H32ZM80,208a16,16,0,1,1,16-16A16,16,0,0,1,80,208Zm81-24H111a32,32,0,0,0-62,0H32V152H176v12.31A32.11,32.11,0,0,0,161,184Zm31,24a16,16,0,1,1,16-16A16,16,0,0,1,192,208Zm48-24H223a32.06,32.06,0,0,0-31-24V128h48Z"/>',
  heart: '<path d="M178,40c-20.65,0-38.73,8.88-50,23.89C116.73,48.88,98.65,40,78,40a62.07,62.07,0,0,0-62,62c0,70,103.79,126.66,108.21,129a8,8,0,0,0,7.58,0C136.21,228.66,240,172,240,102A62.07,62.07,0,0,0,178,40ZM128,214.8C109.74,204.16,32,155.69,32,102A46.06,46.06,0,0,1,78,56c19.45,0,35.78,10.36,42.6,27a8,8,0,0,0,14.8,0c6.82-16.67,23.15-27,42.6-27a46.06,46.06,0,0,1,46,46C224,155.61,146.24,204.15,128,214.8Z"/>',
}

function icon(name, css) {
  return `<svg viewBox="0 0 256 256" fill="currentColor" aria-hidden="true" focusable="false" style="${css}">${ICONS[name]}</svg>`
}

class FurnitureCollection extends HTMLElement {
  connectedCallback() {
    const brand = this.getAttribute('brand')

    if (!brand) {
      console.warn('furniture-collection: missing required "brand" attribute')
      return
    }

    const shadow = this.attachShadow({ mode: 'open' })

    const style = document.createElement('style')
    style.textContent = `
      :host {
        display: block;
        /* Every other shade is mixed off this one, so retinting the card is a
           single custom-property write once the brand resolves. */
        --fc-accent: ${DEFAULT_ACCENT};
        --fc-ink: color-mix(in srgb, var(--fc-accent) 84%, #001512);
        --fc-tint: color-mix(in srgb, var(--fc-accent) 20%, #ffffff);
        --fc-glow: color-mix(in srgb, var(--fc-accent) 34%, transparent);
        --fc-font: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      }

      *, *::before, *::after { box-sizing: border-box; }

      .fc-root { width: 100%; font-family: var(--fc-font); -webkit-font-smoothing: antialiased; }

      .fc-card {
        position: relative;
        background: var(--fc-accent);
        border-radius: 32px;
        padding: 44px 44px 40px;
        box-shadow: 0 18px 40px -10px var(--fc-glow);
        overflow: hidden;
      }

      .fc-inner { position: relative; }

      .fc-eyebrow {
        font-weight: 600; font-size: 0.8125rem; letter-spacing: 0.08em;
        text-transform: uppercase; color: var(--fc-tint); margin: 0 0 12px;
      }

      .fc-title {
        font-weight: 700; font-size: clamp(2rem, 5vw, 3rem); line-height: 1.05;
        letter-spacing: -0.02em; color: #fff; margin: 0 0 12px; text-wrap: balance;
      }

      .fc-lede {
        font-size: 1.125rem; line-height: 1.4; color: var(--fc-tint);
        margin: 0 0 24px; max-width: 32ch;
      }

      .fc-form { display: flex; gap: 12px; flex-wrap: wrap; align-items: stretch; }
      .fc-field { position: relative; flex: 1 1 340px; min-width: 220px; }

      .fc-input {
        width: 100%; height: 60px; padding: 0 20px 0 50px;
        border: 0; border-radius: 999px; background: #fff; color: #1F1B16;
        font-family: var(--fc-font); font-weight: 500; font-size: 1.0625rem;
        text-transform: uppercase; outline: none;
        box-shadow: 0 4px 12px -2px rgba(15, 23, 42, 0.12);
        transition: box-shadow 140ms cubic-bezier(0.22, 1, 0.36, 1);
      }
      .fc-input::placeholder { color: #8E866F; text-transform: none; }
      .fc-input:focus { box-shadow: 0 0 0 3px var(--fc-glow), 0 4px 12px -2px rgba(15, 23, 42, 0.12); }
      .fc-input.fc-invalid { box-shadow: 0 0 0 3px rgba(229, 72, 77, 0.45); }

      .fc-btn {
        display: inline-flex; align-items: center; justify-content: center; gap: 10px;
        flex: 0 0 auto; height: 60px; padding: 0 26px; border: 0; border-radius: 999px;
        cursor: pointer; font-family: var(--fc-font); font-weight: 700; font-size: 1.0625rem;
        line-height: 1; white-space: nowrap; color: var(--fc-ink); background: #fff;
        box-shadow: 0 12px 28px -6px rgba(15, 23, 42, 0.18);
        transition: background 140ms ease, transform 80ms ease;
      }
      .fc-btn:hover { background: #F4FBFA; }
      .fc-btn:active { transform: translateY(1px); }

      .fc-error { display: none; margin-top: 12px; color: #fff; font-weight: 600; font-size: 0.9375rem; }
      .fc-error.fc-show { display: block; }

      .fc-features { display: flex; flex-wrap: wrap; gap: 22px; margin-top: 26px; }
      .fc-feature {
        display: inline-flex; align-items: center; gap: 9px;
        color: #fff; font-weight: 500; font-size: 0.9375rem;
      }

      @media (max-width: 560px) {
        .fc-card { padding: 28px 22px 26px; border-radius: 24px; }
        .fc-field { flex: 1 1 100%; }
        .fc-btn { width: 100%; }
      }

      @media (prefers-reduced-motion: reduce) {
        .fc-input, .fc-btn { transition: none; }
      }
    `

    const root = document.createElement('div')
    root.className = 'fc-root'
    root.innerHTML = `
      <section class="fc-card">
        ${icon('couch', 'position:absolute;top:-8px;right:-12px;width:200px;height:200px;color:rgba(255,255,255,0.10);transform:rotate(-8deg);pointer-events:none;')}
        <div class="fc-inner">
          <div class="fc-eyebrow">Free collection</div>
          <h2 class="fc-title">Book your collection</h2>
          <p class="fc-lede">Tell us where you are and we'll show you when we can come.</p>

          <form class="fc-form" novalidate>
            <div class="fc-field">
              ${icon('pin', 'position:absolute;left:18px;top:50%;transform:translateY(-50%);width:22px;height:22px;color:var(--fc-ink);pointer-events:none;')}
              <input class="fc-input" type="text" autocomplete="postal-code" placeholder="Enter collection postcode" aria-label="Collection postcode" />
            </div>
            <button class="fc-btn" type="submit">Check availability ${icon('arrow', 'width:22px;height:22px;flex:none;')}</button>
          </form>

          <div class="fc-error" role="alert" aria-live="polite">Please enter your postcode</div>

          <div class="fc-features">
            <span class="fc-feature">${icon('coins', 'width:20px;height:20px;flex:none;color:var(--fc-tint);')} Always free to you</span>
            <span class="fc-feature">${icon('truck', 'width:20px;height:20px;flex:none;color:var(--fc-tint);')} Collected from your home</span>
            <span class="fc-feature">${icon('heart', 'width:20px;height:20px;flex:none;color:var(--fc-tint);')} Supports our work</span>
          </div>
        </div>
      </section>
    `

    const form = root.querySelector('.fc-form')
    const input = root.querySelector('.fc-input')
    const errorMsg = root.querySelector('.fc-error')

    input.addEventListener('input', () => {
      // Uppercase in place without moving the caret, so typing mid-string does
      // not jump the cursor to the end.
      const start = input.selectionStart
      const end = input.selectionEnd
      input.value = input.value.toUpperCase()
      input.setSelectionRange(start, end)

      input.classList.remove('fc-invalid')
      errorMsg.classList.remove('fc-show')
    })

    form.addEventListener('submit', (event) => {
      event.preventDefault()
      const postcode = input.value.trim()

      if (!postcode) {
        input.classList.add('fc-invalid')
        errorMsg.classList.add('fc-show')
        input.focus()
        return
      }

      navigateToFlow(this, brand, postcode)
    })

    shadow.appendChild(style)
    shadow.appendChild(root)

    // Async, so the card is on screen immediately and simply retints. A failed
    // or slow lookup leaves the default accent rather than an empty box.
    fetch(`${ORIGIN}/embed/v1/brands/${encodeURIComponent(brand)}`)
      .then((response) => (response.ok ? response.json() : null))
      .then((config) => {
        if (!config) return
        if (config.accent_color && HEX_COLOR.test(config.accent_color)) {
          const accent = document.createElement('style')
          accent.textContent = `:host { --fc-accent: ${config.accent_color}; }`
          shadow.appendChild(accent)
        }
      })
      .catch(() => {})
  }
}

// A full-page fade before navigating, so the jump from the charity's site to
// ours does not read as a broken link.
function navigateToFlow(host, brand, postcode) {
  const url = `${ORIGIN}/embed/v1/request?brand=${encodeURIComponent(brand)}&postcode=${encodeURIComponent(postcode)}`

  if (document.getElementById('fc-transition-overlay')) return

  const accent =
    getComputedStyle(host).getPropertyValue('--fc-accent').trim() || DEFAULT_ACCENT

  const overlay = document.createElement('div')
  overlay.id = 'fc-transition-overlay'
  overlay.style.cssText = `
    position: fixed; inset: 0; background: #fff; display: flex;
    align-items: center; justify-content: center; z-index: 2147483647;
    opacity: 0; transition: opacity ${TRANSITION_MS}ms ease;
  `

  const spinner = document.createElement('div')
  spinner.className = 'fc-spinner'
  spinner.style.cssText = `
    width: 32px; height: 32px; border: 3px solid rgba(15, 23, 42, 0.12);
    border-top-color: ${accent}; border-radius: 50%;
    animation: fc-spin 0.8s linear infinite;
  `

  if (!document.getElementById('fc-spinner-style')) {
    const spinnerStyle = document.createElement('style')
    spinnerStyle.id = 'fc-spinner-style'
    spinnerStyle.textContent = `
      @keyframes fc-spin { to { transform: rotate(360deg); } }
      @media (prefers-reduced-motion: reduce) {
        #fc-transition-overlay { transition-duration: 0ms !important; }
        .fc-spinner { animation-duration: 0ms !important; }
      }
    `
    document.head.appendChild(spinnerStyle)
  }

  overlay.appendChild(spinner)
  document.body.appendChild(overlay)
  overlay.offsetHeight // force reflow so the transition actually runs
  overlay.style.opacity = '1'

  // Whichever fires first. transitionend alone would strand anyone whose
  // browser skips the transition.
  let navigated = false
  const navigate = () => {
    if (navigated) return
    navigated = true
    window.location.href = url
  }
  overlay.addEventListener('transitionend', navigate)
  setTimeout(navigate, TRANSITION_MS + 50)
}

// Restoring from bfcache re-shows the page with the overlay still painted over
// it, which looks like a hang. Clear it.
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return
  const overlay = document.getElementById('fc-transition-overlay')
  if (overlay) overlay.remove()
})

if (!customElements.get('furniture-collection')) {
  customElements.define('furniture-collection', FurnitureCollection)
}
